/**
 * Draws ORB keypoints as small filled dots with a thin scale ring on an
 * overlay canvas sized to match the preview image. Keypoint coords come in
 * normalized 0–1 so we don't need to know the analysis-space resolution.
 */

import type { Keypoint } from 'trace-viewer/image-utils';

/**
 * Cap on visible keypoints. ORB returns up to 1500 keypoints, but drawing
 * all of them obscures the image. We render the top N by response, which
 * are also the most diagnostically useful (strongest features = ones a
 * tracker actually relies on).
 */
const MAX_VISIBLE = 400;

/** Single bright marker color, chosen to pop on both light and dark images. */
const MARKER_COLOR = 'rgba(255, 64, 192, 0.95)'; // magenta
const RING_COLOR = 'rgba(255, 64, 192, 0.55)';

export function drawKeypointOverlay(
  canvas: HTMLCanvasElement,
  img: HTMLImageElement,
  keypoints: readonly Keypoint[],
): void {
  const dpr = window.devicePixelRatio || 1;
  const w = img.clientWidth;
  const h = img.clientHeight;
  if (w === 0 || h === 0) return;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  if (keypoints.length === 0) return;

  // Pick the top-N by response so dense images don't paint solid blocks.
  const top = keypoints.length <= MAX_VISIBLE
    ? keypoints
    : [...keypoints].sort((a, b) => b.response - a.response).slice(0, MAX_VISIBLE);

  const shortEdge = Math.min(w, h);
  ctx.lineWidth = 1;
  for (const kp of top) {
    const x = kp.x * w;
    const y = kp.y * h;
    // Scale ring — ORB's `size` is the patch diameter at the detected scale.
    const ringR = Math.max(4, Math.min(28, (kp.size * shortEdge) / 2));
    ctx.strokeStyle = RING_COLOR;
    ctx.beginPath();
    ctx.arc(x, y, ringR, 0, Math.PI * 2);
    ctx.stroke();
    // Filled dot at the keypoint center for precise localization.
    ctx.fillStyle = MARKER_COLOR;
    ctx.beginPath();
    ctx.arc(x, y, 2, 0, Math.PI * 2);
    ctx.fill();
  }
}
